import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { AppRoleReq } from '@shared/interfaces/app.role.req';
import { PLATFORM_ADMIN } from '@shared/components/header/header.component.types';
import { MsalService } from '@azure/msal-angular';
import { AppStorageService } from './app-storage.service';
import { PermissionsService } from '@core/permission/permissions.service';
import { filter, map, find } from 'lodash';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';
import { AppService } from './app.service';
import { SAS_SCOPES } from './../../app.config';
import { CommonService } from './common.service';
import { isEmpty } from 'lodash';
import { Router } from '@angular/router';
import { SharedService } from '@core-services/shared.service';
import { AppConfigService } from '@core-services/app-config.service';
import { DataService } from 'src/app/core/services/data.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  entryAPI = environment.entryAPI;
  public appList: any;
  public roles: any;
  public permissionData: any;
  constructor(
    private http: HttpClient,
    private authService: MsalService,
    private appStorage: AppStorageService,
    private permissionServ: PermissionsService,
    private appService: AppService,
    private commonService: CommonService,
    private router: Router,
    private sharedService: SharedService,
    private appConfigService: AppConfigService,
    private dataService: DataService
  ) {}

  getUserRolePermissions(data: string): Promise<any> {
    return firstValueFrom(
      this.http.get(
        this.entryAPI + 'roles' + `/permissions/` + encodeURIComponent(data)
      )
    );
  }

  getRoleAppList(data?: unknown): Observable<any> {
    const query = '?status=' + true;
    return this.http.get(this.entryAPI + `apps/` + query);
  }

  getCurrentUserRole(): any {
    const currentRole = localStorage.getItem('selectedRole');
    const roles = currentRole ? JSON.parse(currentRole) : '';
    return roles;
  }

  userUnauthorized(): Observable<any> {
    return this.http.post(this.entryAPI + `users?action=unauthorized`, {});
  }

  getHeaderAlert(): Observable<any> {
    return this.http.get(this.entryAPI + 'sites');
  }
  getRolesByApplication(req: AppRoleReq): Observable<any> {
    return this.http.post(this.entryAPI + `roles/${req.applicationId}`, {
      roles: req.roles,
    });
  }
  getRoles(req: AppRoleReq): Observable<any> {
    return this.http.post(this.entryAPI + `roles/`, { roles: req.roles });
  }
  getRolesappOnly(req: AppRoleReq): Observable<any> {
    return this.http.post(this.entryAPI + `roles/`, { roles: req.roles });
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async resolveActiveAccount(
    maxAttempts = 20,
    retryDelayMs = 150
  ): Promise<any> {
    let activeAccount = this.authService.instance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.authService.instance.setActiveAccount(accounts[0]);
        return accounts[0];
      }

      await this.sleep(retryDelayMs);
      activeAccount = this.authService.instance.getActiveAccount();
      if (activeAccount) {
        return activeAccount;
      }
    }

    return null;
  }

  public updateData(): Observable<any> {
    return new Observable((process) => {
      void this.resolveActiveAccount().then((token) => {
        if (!token) {
          process.next(false);
          process.complete();
          return;
        }

        this.appList = this.getRoleAppList({})
          .pipe(retry({ count: 2, delay: 800 }))
          .subscribe(
            (appList) => {
              try {
                this.appList = appList;
                const roleList = token.idTokenClaims?.roles;
                const reqData: AppRoleReq = {
                  applicationId: '',
                  roles: roleList,
                };
                this.getRoles(reqData)
                  .pipe(retry({ count: 2, delay: 800 }))
                  .subscribe(
                    (roleData) => {
                      try {
                        this.roles = roleData;
                        const appsData = this.updatePermission();
                        process.next(appsData);
                        process.complete();
                      } catch (error) {
                        process.next(false);
                        process.complete();
                      }
                    },
                    (err) => {
                      process.next(false);
                      process.complete();
                    }
                  );
              } catch (err) {
                this.router.navigate(['/access-denied']);
                process.next(false);
                process.complete();
              }
            },
            (err) => {
              this.dataService.isNavigationLoad.next(true);
              this.router.navigate(['/access-denied']);
              process.next(false);
              process.complete();
            }
          );
      });
    });
  }

  updatePermission() {
    try {
      if (this.appList && this.appList.data && this.appList.data.length) {
        let appListLc = this.appList.data;
        const res = this.roles;
        let availableRole = [];
        let listOfRoleName = [];
        let selectedApp;
        let selectedAppId: string | null = null;
        appListLc = filter(appListLc, (item) => {
          if (res.data.filter((role: any) => role.appId === item.id)[0]) {
            return true;
          }
        });
        if (isEmpty(appListLc)) {
          this.dataService.isNavigationLoad.next(true);
          this.router.navigate(['/access-denied']);
        } else {
          if (!localStorage.getItem('selectedApp')) {
            selectedApp = appListLc[0];
            selectedAppId = selectedApp.id;
            localStorage.setItem('selectedApp', selectedApp.id);
            localStorage.setItem('selectedAppName', selectedApp.name);
          } else {
            selectedAppId = localStorage.getItem('selectedApp');
          }
          this.sharedService.selectedAppObjr.next(selectedApp);
          if (selectedAppId) {
            availableRole = filter(res.data, (item) => {
              return (
                item.appId === selectedAppId || item.name === PLATFORM_ADMIN
              );
            });
          }
          listOfRoleName = map(availableRole, 'name');
          let selectedRole = this.appStorage.getRole();
          // Recover from a stale stored app selection: the user no longer has a
          // role on the previously selected app, but still has access to other
          // apps. Fall back to the first app they can access instead of wrongly
          // showing the access-denied page on refresh.
          if (isEmpty(availableRole) && !isEmpty(appListLc)) {
            selectedApp = appListLc[0];
            selectedAppId = selectedApp.id;
            localStorage.setItem('selectedApp', selectedApp.id);
            localStorage.setItem('selectedAppName', selectedApp.name);
            localStorage.removeItem('selectedRole');
            availableRole = filter(res.data, (item) => {
              return (
                item.appId === selectedAppId || item.name === PLATFORM_ADMIN
              );
            });
            listOfRoleName = map(availableRole, 'name');
            selectedRole = null;
          }
          if (isEmpty(availableRole)) {
            this.router.navigate(['/access-denied']);
          }
          selectedRole = selectedRole ? selectedRole : availableRole[0];
          this.appStorage.setAppRoles(listOfRoleName);
          this.appStorage.setRole(JSON.stringify(selectedRole));

          const singleRole: any = find(availableRole, {
            name: selectedRole.name,
          });
          const permission = map(singleRole.permissions, 'perm');
          this.permissionData = { data: permission };
          this.permissionServ.setPermission(permission);
          this.permissionServ.notifyPermission.next(permission);
          return {
            roles: availableRole,
            apps: appListLc,
            permissions: permission,
          };
        }
      } else {
        this.router.navigate(['/access-denied']);
      }
    } catch (err) {
      this.router.navigate(['/access-denied']);
    }
  }

  getAppConfig(): any {
    return new Promise((resol, reject) => {
      this.appService.getAppConfig().subscribe((res: any) => {
        if (res.constants) {
          let extensions = {};
          if (res.data[0].extensions) {
            extensions = res.data[0].extensions;
          }
          res.constants.extensions = extensions;
          this.appConfigService.setAppConfigData(res.constants);
          this.sharedService.setAppConfig(res.constants);
        }
        let readStorageConfig = res.data[0].storages.filter((element: any) => {
          return element.scope === SAS_SCOPES.readOnly;
        });

        const editStorageConfig = res.data[0].storages.filter(
          (element: any) => {
            return element.scope === SAS_SCOPES.editOnly;
          }
        );

        readStorageConfig =
          readStorageConfig && readStorageConfig.length
            ? readStorageConfig
            : res.data[0].storages;

        let sasToken = '';
        let editSasToken = '';
        let storageAccount = '';
        let containerName = '';

        if (readStorageConfig && readStorageConfig[0]) {
          sasToken = this.commonService.decrypt(
            readStorageConfig[0].params.sas
          );
          storageAccount = readStorageConfig[0].params.storageAccount;
        }
        this.appStorage.setSAS(sasToken);
        this.appStorage.setDatatypes(res.constants.dataType);
        if (editStorageConfig && editStorageConfig[0]) {
          editSasToken = this.commonService.decrypt(
            editStorageConfig[0].params.sas
          );
          containerName = editStorageConfig[0].params.containerName;
        }
        this.appStorage.setEditSAS(editSasToken);
        this.appStorage.setStorageAccount(storageAccount);
        this.appStorage.setStorageContainer(containerName);
        if (editStorageConfig && editStorageConfig[0]) {
          this.appStorage.setStorageFileShare(
            editStorageConfig[0].params.shareName
          );
          this.appStorage.setFileShareRootDirectory(
            editStorageConfig[0].params.shareRootDirectory
          );
        }
        resol(res);
      });
    });
  }
}
